import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, Filter, Users as UsersIcon, Mail, UserPlus, Plus, X, Check, Copy } from "lucide-react";
import PageHeader from "@/components/common/PageHeader";
import Avatar from "@/components/common/Avatar";
import EmptyState from "@/components/common/EmptyState";
import { PatientStatusBadge } from "@/components/common/StatusBadge";
import RiskBadge from "@/components/common/RiskBadge";
import { listLinkedPatients, registerPatient, listClinicMembers } from "@/lib/db";
import { calcAge, formatDate } from "@/lib/format";

const STATUSES = [
  { v: "all", label: "Todos" },
  { v: "active", label: "Ativos" },
  { v: "pending", label: "Pendentes" },
  { v: "blocked", label: "Bloqueados" },
];

import { useAuth } from "@/contexts/AuthContext";

export default function Patients() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialQ = new URLSearchParams(location.search).get("q") || "";

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState(initialQ);
  const [status, setStatus] = useState("active");
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const { clinicId, user, clinic } = useAuth();

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        setList(await listLinkedPatients({ 
          q: q || undefined, 
          status, 
          clinic_id: clinicId,
          user_role: clinic?.my_role 
        }));
      } catch (err) {
        console.error("Erro ao carregar pacientes:", err);
        setError(err.message || "Erro desconhecido");
      } finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, status, clinicId, clinic?.my_role]);

  if (error) {
    return (
      <div className="ds-card p-10 text-center">
        <div className="text-[hsl(var(--danger-text))] font-bold mb-2">Erro ao carregar pacientes</div>
        <div className="text-sm text-[hsl(var(--text-secondary))]">{error}</div>
        <button onClick={() => window.location.reload()} className="mt-4 ds-btn-secondary h-9 text-xs">Tentar novamente</button>
      </div>
    );
  }

  return (
    <div className="ds-fade-up">
      <PageHeader
        eyebrow="Cadastro"
        title="Pacientes"
        description="Gerencie os pacientes da sua clínica."
        actions={
          <div className="flex gap-2">
            <Link to="/app/convites" className="ds-btn-secondary hidden md:flex">
              <Mail className="w-4 h-4" /> Convites
            </Link>
            <button onClick={() => setIsRegisterOpen(true)} className="ds-btn-primary">
              <UserPlus className="w-4 h-4" /> Cadastrar paciente
            </button>
          </div>
        }
      />

      <RegisterPatientDrawer 
        isOpen={isRegisterOpen} 
        onClose={() => setIsRegisterOpen(false)}
        onSuccess={() => {
          setIsRegisterOpen(false);
          window.location.reload(); // Quick refresh
        }}
        clinicId={clinicId}
      />

      <div className="ds-card p-4 flex flex-col md:flex-row md:items-center gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-tertiary))]" />
          <input data-testid="patients-search" value={q} onChange={e => setQ(e.target.value)}
                 className="ds-input pl-9" placeholder="Buscar por nome..." />
        </div>
        <div className="flex items-center gap-1 p-1 rounded-lg bg-[hsl(var(--bg-base))] border border-[hsl(var(--border-default))]">
          <Filter className="w-4 h-4 mx-2 text-[hsl(var(--text-tertiary))]" />
          {STATUSES.map(s => (
            <button key={s.v} data-testid={`filter-status-${s.v}`} onClick={() => setStatus(s.v)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                status === s.v ? "bg-[hsl(var(--brand))] text-white" : "text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--bg-hover))]"
              }`}>{s.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="ds-card p-10 text-center text-sm text-[hsl(var(--text-tertiary))]">Carregando...</div>
      ) : list.length === 0 ? (
        <EmptyState testid="patients-empty" icon={UsersIcon} title="Nenhum paciente"
                    description="Compartilhe um código de convite ou aprove solicitações pendentes para começar."
                    action={<Link to="/app/convites" className="ds-btn-primary"><Mail className="w-4 h-4" /> Gerar convite</Link>} />
      ) : (
        <>
          {/* Mobile View: Cards (Visible on mobile, hidden on desktop) */}
          <div className="grid grid-cols-1 gap-4 md:hidden" data-testid="patients-cards">
            {list.map((p) => (
              <div key={p.id} data-testid={`patient-card-${p.id}`}
                   onClick={() => navigate(`/app/pacientes/${p.id}`)}
                   className="ds-card p-5 active:scale-[0.98] cursor-pointer transition-transform relative overflow-hidden group">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Avatar name={p.full_name} size={48} />
                    <div className="min-w-0">
                      <div className="text-base font-bold text-[hsl(var(--text-primary))] truncate">{p.full_name}</div>
                      <div className="text-xs text-[hsl(var(--text-tertiary))] truncate">
                        {p.is_ghost && <span className="text-[hsl(var(--warning-text))] font-medium mr-1">● Aguardando acesso:</span>}
                        {p.email || p.phone || "Sem contato"}
                      </div>
                    </div>
                  </div>
                  <PatientStatusBadge status={p.status} />
                </div>

                <div className="grid grid-cols-2 gap-4 py-4 border-y border-[hsl(var(--border-divider))] mb-4">
                  <div className="space-y-1">
                    <div className="text-[10px] uppercase font-bold text-[hsl(var(--text-tertiary))] tracking-widest">Idade</div>
                    <div className="text-sm font-semibold">{calcAge(p.birth_date) ? `${calcAge(p.birth_date)} anos` : "—"}</div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-[10px] uppercase font-bold text-[hsl(var(--text-tertiary))] tracking-widest">Cadastro</div>
                    <div className="text-sm font-semibold">{formatDate(p.requested_at || p.created_at)}</div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5 items-center">
                  {(p.risk_flags || []).map(f => <RiskBadge key={f} flag={f} />)}
                  {(p.tags || []).slice(0, 4).map(t => <span key={t} className="ds-badge ds-badge-brand">{t}</span>)}
                  {p.phone && <div className="text-[11px] text-[hsl(var(--text-tertiary))] ml-auto">{p.phone}</div>}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop View: Table (Hidden on mobile, visible on desktop) */}
          <div className="hidden md:block ds-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[hsl(var(--bg-base))]/60">
                    <Th>Paciente</Th><Th>Status</Th><Th>Idade</Th>
                    <Th>Tags clínicas</Th><Th>Sinalizadores</Th>
                    <Th>Última atualização</Th><Th>Contato</Th>
                  </tr>
                </thead>
                <tbody data-testid="patients-table-body">
                  {list.map((p) => (
                    <tr key={p.id} data-testid={`patient-row-${p.id}`}
                        onClick={() => navigate(`/app/pacientes/${p.id}`)}
                        className="border-t border-[hsl(var(--border-divider))] transition-colors hover:bg-[hsl(var(--bg-hover))]/50 cursor-pointer">
                      <Td>
                        <div className="flex items-center gap-3">
                          <Avatar name={p.full_name} size={36} />
                          <div>
                            <div className="text-sm font-medium">{p.full_name}</div>
                            <div className="text-xs text-[hsl(var(--text-tertiary))]">
                              {p.is_ghost && <span className="text-[hsl(var(--warning-text))] font-medium mr-1 text-[10px]">● Pendente:</span>}
                              {p.email || "—"}
                            </div>
                          </div>
                        </div>
                      </Td>
                      <Td><PatientStatusBadge status={p.status} /></Td>
                      <Td className="text-sm">{calcAge(p.birth_date) ? `${calcAge(p.birth_date)} a` : "—"}</Td>
                      <Td>
                        <div className="flex flex-wrap gap-1">
                          {(p.tags || []).slice(0,3).map(t => <span key={t} className="ds-badge ds-badge-brand">{t}</span>)}
                        </div>
                      </Td>
                      <Td>
                        <div className="flex flex-wrap gap-1">
                          {(p.risk_flags || []).slice(0,2).map(f => <RiskBadge key={f} flag={f} />)}
                        </div>
                      </Td>
                      <Td className="text-sm text-[hsl(var(--text-secondary))]">{formatDate(p.requested_at || p.updated_at || p.created_at)}</Td>
                      <Td className="text-sm text-[hsl(var(--text-secondary))]">{p.phone || "—"}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
    </>
  )}
</div>
  );
}

function Th({ children }) { return <th className="text-left px-4 py-3 ds-overline whitespace-nowrap">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-4 ${className}`}>{children}</td>; }

function RegisterPatientDrawer({ isOpen, onClose, onSuccess, clinicId }) {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [professionals, setProfessionals] = useState([]);
  const [formData, setFormData] = useState({ full_name: "", email: "", phone: "", professional_id: "" });
  const [inviteResult, setInviteResult] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      if (clinicId) {
        listClinicMembers(clinicId).then(data => {
          // Only professionals
          const pros = data.filter(m => m.role === 'professional' || m.role === 'admin');
          setProfessionals(pros);
          if (pros.length > 0) setFormData(prev => ({ ...prev, professional_id: pros[0].profile_id }));
        });
      } else if (profile) {
        // Solo mode: O próprio profissional é o responsável
        setProfessionals([{ profile_id: profile.id, role: 'professional', profile: profile }]);
        setFormData(prev => ({ ...prev, professional_id: profile.id }));
      }
    }
  }, [isOpen, clinicId, profile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await registerPatient({ ...formData, clinic_id: clinicId });
      setInviteResult(res);
    } catch (err) {
      console.error(err);
      alert("Erro ao cadastrar: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex justify-end">
      {/* Overlay mais escuro para foco total */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-lg bg-[hsl(var(--bg-paper))] shadow-2xl flex flex-col h-full animate-in slide-in-from-right duration-300">
        <div className="flex items-center justify-between p-6 border-b border-[hsl(var(--border))] bg-[hsl(var(--bg-base))]">
          <div>
            <h2 className="text-xl font-bold text-[hsl(var(--text-primary))]">Novo Cadastro</h2>
            <p className="text-xs text-[hsl(var(--text-secondary))] mt-1">Registre um novo paciente na clínica</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[hsl(var(--bg-paper))] rounded-full transition-colors text-[hsl(var(--text-secondary))]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-8 bg-[hsl(var(--bg-paper))]">
          {!inviteResult ? (
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="space-y-2">
                <label className="text-[11px] font-black text-[hsl(var(--text-tertiary))] uppercase tracking-[0.2em]">Dados Pessoais</label>
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <span className="text-[10px] font-bold text-[hsl(var(--text-secondary))] uppercase">Nome Completo</span>
                    <input required value={formData.full_name} onChange={e => setFormData({ ...formData, full_name: e.target.value })}
                           className="ds-input bg-[hsl(var(--bg-base))] border-[hsl(var(--border))]" placeholder="Ex: João Silva" />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <span className="text-[10px] font-bold text-[hsl(var(--text-secondary))] uppercase">Telefone</span>
                      <input value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })}
                             className="ds-input bg-[hsl(var(--bg-base))] border-[hsl(var(--border))]" placeholder="(11) 99999-9999" />
                    </div>
                    <div className="space-y-1.5">
                      <span className="text-[10px] font-bold text-[hsl(var(--text-secondary))] uppercase">E-mail (opcional)</span>
                      <input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })}
                             className="ds-input bg-[hsl(var(--bg-base))] border-[hsl(var(--border))]" placeholder="joao@email.com" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[11px] font-black text-[hsl(var(--text-tertiary))] uppercase tracking-[0.2em]">Configuração de Acesso</label>
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <span className="text-[10px] font-bold text-[hsl(var(--text-secondary))] uppercase">Profissional Responsável</span>
                    <select 
                      required 
                      value={formData.professional_id} 
                      onChange={e => setFormData({ ...formData, professional_id: e.target.value })}
                      className="ds-input bg-[hsl(var(--bg-base))] border-[hsl(var(--border))]"
                    >
                      {professionals.map(p => {
                        const name = p.profile?.full_name || "Sem nome";
                        const role = p.role === 'professional' ? 'Profissional' : 'Admin';
                        return (
                          <option key={p.profile_id} value={p.profile_id}>
                            {`${name} (${role})`}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div className="p-4 bg-[hsl(var(--brand-muted))/10] rounded-xl border border-[hsl(var(--brand-border))/30] bg-blue-50/50">
                    <div className="flex gap-3">
                      <div className="w-1 h-auto bg-[hsl(var(--brand-base))] rounded-full" />
                      <p className="text-[11px] text-[hsl(var(--text-primary))] leading-relaxed italic">
                        Para garantir a conformidade ética e a LGPD, os dados clínicos deste paciente serão inicialmente visíveis apenas para o profissional selecionado e para a equipe de gestão da clínica.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <button type="submit" disabled={loading} className="ds-button-primary w-full py-4 text-sm font-bold shadow-lg shadow-[hsl(var(--brand-base))/20]">
                {loading ? "Processando..." : "Finalizar Cadastro de Paciente"}
              </button>
            </form>
          ) : (
            <div className="text-center space-y-6 ds-fade-up">
              <div className="w-16 h-16 bg-[hsl(var(--success-muted))] text-[hsl(var(--success-text))] rounded-full flex items-center justify-center mx-auto">
                <Check className="w-8 h-8" />
              </div>
              <div>
                <h3 className="text-lg font-bold">Paciente Cadastrado!</h3>
                <p className="text-sm text-[hsl(var(--text-secondary))] mt-1">
                  O prontuário já está ativo para atendimento.
                </p>
              </div>

              <div className="p-4 bg-[hsl(var(--bg-base))] rounded-xl border border-[hsl(var(--border-default))] text-left space-y-3">
                <div>
                  <div className="text-[10px] uppercase font-bold text-[hsl(var(--text-tertiary))]">Código de Acesso do Paciente</div>
                  <div className="flex items-center justify-between mt-1">
                    <div className="text-xl font-mono font-bold tracking-widest text-[hsl(var(--brand-text))]">{inviteResult.code}</div>
                    <button 
                      onClick={() => {
                        navigator.clipboard.writeText(inviteResult.code);
                        alert("Código copiado!");
                      }}
                      className="p-2 hover:bg-[hsl(var(--bg-secondary))] rounded-lg transition-colors"
                      title="Copiar código"
                    >
                      <Copy className="w-4 h-4 text-[hsl(var(--text-tertiary))]" />
                    </button>
                  </div>
                </div>
                <p className="text-[11px] text-[hsl(var(--text-tertiary))] leading-relaxed">
                  O paciente poderá usar este código para criar a conta dele e acessar o portal. Enquanto isso, você já pode registrar sessões e anamneses.
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <button onClick={() => navigate(`/app/pacientes/${inviteResult.metadata?.profile_id}`)} className="w-full ds-btn-primary">
                  Abrir Prontuário Agora
                </button>
                <button onClick={onSuccess} className="w-full ds-btn-secondary">
                  Fechar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
